import { useMemo, useState } from 'react';

import { OpportunityResultRow } from './api';

interface OpportunityMatrixProps {
  rows: OpportunityResultRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const PLOT = { x0: 80, x1: 720, y0: 40, y1: 520 };
const WIDTH = 760;
const HEIGHT = 600;

export default function OpportunityMatrix({ rows, selectedId, onSelect }: OpportunityMatrixProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const orderedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const activeA = a.status === 'active' ? 1 : 0;
        const activeB = b.status === 'active' ? 1 : 0;
        return activeA - activeB || priorityValue(a) - priorityValue(b);
      }),
    [rows]
  );
  const hovered = hoveredId ? rows.find((row) => row.id === hoveredId) : null;

  return (
    <section className="bg-gray-50 px-4 py-7 dark:bg-dark-bg">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-subtitle text-h2 text-charcoal dark:text-gray-100">
              Updated Pain / Gain Matrix
            </h2>
            <p className="max-w-4xl text-small text-gray-600 dark:text-gray-300">
              Every opportunity is plotted by computed feasibility and value. Top 10 active items have a navy ring and rank number.
            </p>
          </div>
          <p className="text-small text-gray-500 dark:text-gray-400">
            Hover for scores. Click a circle to open its detail row.
          </p>
        </div>
        <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={`Pain gain matrix of ${rows.length} opportunities`}
            className="h-[560px] max-h-[70vh] min-h-[420px] w-full"
          >
            <defs>
              <linearGradient id="ranker-matrix-bg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#FEE2D8" />
                <stop offset="48%" stopColor="#FFFFFF" />
                <stop offset="100%" stopColor="#E8F3FF" />
              </linearGradient>
            </defs>
            <rect x={PLOT.x0} y={PLOT.y0} width={PLOT.x1 - PLOT.x0} height={PLOT.y1 - PLOT.y0} fill="url(#ranker-matrix-bg)" />
            <path d={`M ${PLOT.x0} ${PLOT.y1} H ${PLOT.x1} M ${PLOT.x0} ${PLOT.y1} V ${PLOT.y0}`} stroke="#111827" strokeWidth="1.5" />
            <path d={`M ${(PLOT.x0 + PLOT.x1) / 2} ${PLOT.y0} V ${PLOT.y1} M ${PLOT.x0} ${(PLOT.y0 + PLOT.y1) / 2} H ${PLOT.x1}`} stroke="#CBD5E1" strokeDasharray="6 6" />
            <text x={PLOT.x0 + 12} y={PLOT.y0 + 24} className="fill-gray-500 text-[13px] font-semibold">Higher value</text>
            <text x={PLOT.x1 - 180} y={PLOT.y1 - 18} className="fill-gray-500 text-[13px] font-semibold">Lower execution pain</text>
            <text x={PLOT.x1 - 178} y={PLOT.y0 + 24} className="fill-[#B8371E] text-[14px] font-bold">High gain / lower pain</text>
            <text x={PLOT.x0 + 14} y={PLOT.y1 - 18} className="fill-gray-500 text-[13px]">Higher pain / lower gain</text>
            {orderedRows.map((row) => (
              <MatrixDot
                key={row.id}
                row={row}
                selected={row.id === selectedId}
                onSelect={onSelect}
                onHover={setHoveredId}
              />
            ))}
          </svg>
          {hovered && (
            <div className="absolute right-4 top-4 max-w-sm rounded-md bg-gray-950 px-3 py-2 text-small text-white shadow-lg">
              <div className="font-semibold">{hovered.id} · {hovered.name}</div>
              <div className="mt-1 text-gray-300">
                {hovered.source ?? 'unknown'} · {hovered.status ?? 'active'}
              </div>
              <div className="mt-2 border-t border-white/20 pt-2 text-[#FFD0BF]">
                Rank #{hovered.active_rank ?? hovered.rank ?? '—'} · Gain {hovered.gain_score} · Pain {hovered.pain_score} · Priority {formatScore(priorityValue(hovered))}{hovered.priority_score_source === 'source_indexes' ? '*' : ''}
              </div>
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-small text-gray-600 dark:text-gray-300">
          <LegendSwatch className="bg-[#F2967B] ring-[#DC4828]" label="Active" />
          <LegendSwatch className="bg-[#DBEAFE] ring-[#3B82F6]" label="In-flight" />
          <LegendSwatch className="bg-gray-300 ring-gray-500" label="Deferred / shipped" />
          <LegendSwatch className="bg-[#F2967B] ring-[#1A2540] ring-[3px]" label="Top 10" />
        </div>
      </div>
    </section>
  );
}

function MatrixDot({
  row,
  selected,
  onSelect,
  onHover,
}: {
  row: OpportunityResultRow;
  selected: boolean;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
}) {
  const feasibility = 100 - row.pain_score;
  const cx = PLOT.x0 + (feasibility / 100) * (PLOT.x1 - PLOT.x0) + jitter(row.id).dx;
  const cy = PLOT.y1 - (row.gain_score / 100) * (PLOT.y1 - PLOT.y0) + jitter(row.id).dy;
  const style = dotStyle(row.status);
  const isTop10 = Boolean(row.is_top_10_active);
  const radius = isTop10 ? style.radius + 4 : style.radius;
  return (
    <g
      className="cursor-pointer"
      onClick={() => onSelect(row.id)}
      onMouseEnter={() => onHover(row.id)}
      onMouseLeave={() => onHover(null)}
      role="button"
      aria-label={`${row.name}, gain ${row.gain_score}, pain ${row.pain_score}`}
    >
      <circle
        cx={cx}
        cy={cy}
        r={selected ? radius + 3 : radius}
        fill={style.fill}
        stroke={selected || isTop10 ? '#1A2540' : style.stroke}
        strokeWidth={selected || isTop10 ? 3 : 1.5}
      />
      {row.status === 'active' && row.active_rank && (isTop10 || radius >= 12) && (
        <text x={cx} y={cy + 4} textAnchor="middle" className="pointer-events-none fill-white text-[11px] font-bold">
          {row.active_rank}
        </text>
      )}
    </g>
  );
}

function dotStyle(status: string | null | undefined) {
  if (status === 'inflight') return { fill: '#DBEAFE', stroke: '#3B82F6', radius: 13 };
  if (status === 'deferred' || status === 'shipped') return { fill: '#E5E7EB', stroke: '#6B7280', radius: 10 };
  return { fill: '#F2967B', stroke: '#DC4828', radius: 13 };
}

function jitter(id: string) {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = ((hash << 5) - hash + id.charCodeAt(index)) | 0;
  }
  const angle = (Math.abs(hash) % 360) * (Math.PI / 180);
  const distance = (Math.abs(hash >> 8) % 16) + 6;
  return { dx: Math.cos(angle) * distance, dy: Math.sin(angle) * distance };
}

function priorityValue(row: OpportunityResultRow) {
  return row.priority_score ?? row.composite_score;
}

function formatScore(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`h-3 w-3 rounded-full ring-2 ${className}`} />
      {label}
    </span>
  );
}
